import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Coins, TrendingUp, TrendingDown, Loader2, Save } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { timeAgo } from "@/lib/constants";
import { Avatar } from "@/components/Avatar";
import { CoinBadge } from "@/components/CoinBadge";
import { IssueCard } from "@/components/IssueCard";
import { AuthModal } from "@/components/AuthModal";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Profile() {
  const { id } = useParams();
  const { user, patchUser } = useAuth();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const isMe = user && user.id === id;

  const load = useCallback(async () => {
    const { data } = await api.get(`/users/${id}`);
    setData(data);
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (isMe) api.get("/coins/history").then(({ data }) => setHistory(data)).catch(() => {});
  }, [isMe]);

  if (!data) {
    return <div className="grid place-items-center py-24 text-muted-foreground"><Loader2 className="animate-spin" size={28} /></div>;
  }

  const p = data.user;
  const updateIssue = (u, list) => setData((d) => ({ ...d, [list]: d[list].map((i) => (i.id === u.id ? u : i)) }));

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 sm:py-8">
      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="h-24 chess-grid bg-background/60 relative">
          <div className="tricolor-bar h-1 w-full absolute bottom-0" />
        </div>
        <div className="p-5 -mt-12">
          <div className="flex items-end justify-between gap-4">
            <Avatar seed={p.avatar_seed} alias={p.alias} size={84} className="ring-4 ring-card" />
            {isMe && (
              <Button data-testid="edit-profile-button" variant="outline" size="sm" onClick={() => setEditOpen(true)} className="gap-1.5">
                <Pencil size={14} /> Edit
              </Button>
            )}
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <h1 data-testid="profile-alias-badge" className="font-display font-extrabold text-2xl tracking-tight">{p.alias}</h1>
            {p.role === "admin" && <span className="text-[11px] font-semibold rounded-full bg-navy/40 border border-navy px-2 py-0.5 text-white">Party Desk</span>}
            <CoinBadge value={p.coins} />
          </div>
          {p.bio && <p className="mt-2 text-sm text-muted-foreground">{p.bio}</p>}
          <div className="mt-2 text-xs text-muted-foreground font-mono">Citizen since {timeAgo(p.created_at)}</div>
        </div>
      </div>

      <Tabs defaultValue="issues" className="mt-6">
        <TabsList className={`grid w-full ${isMe ? "grid-cols-3" : "grid-cols-2"}`}>
          <TabsTrigger value="issues" data-testid="profile-tab-issues">Issues ({data.issues.length})</TabsTrigger>
          <TabsTrigger value="supported" data-testid="profile-tab-supported">Backed ({data.supported.length})</TabsTrigger>
          {isMe && <TabsTrigger value="wallet" data-testid="profile-tab-wallet">Coin Ledger</TabsTrigger>}
        </TabsList>

        <TabsContent value="issues" className="mt-4 space-y-4">
          {data.issues.length === 0 && <Empty text="No issues raised yet." />}
          {data.issues.map((i) => <IssueCard key={i.id} issue={i} onUpdate={(u) => updateIssue(u, "issues")} onRequireAuth={() => setAuthOpen(true)} />)}
        </TabsContent>

        <TabsContent value="supported" className="mt-4 space-y-4">
          {data.supported.length === 0 && <Empty text="No issues backed yet." />}
          {data.supported.map((i) => <IssueCard key={i.id} issue={i} onUpdate={(u) => updateIssue(u, "supported")} onRequireAuth={() => setAuthOpen(true)} />)}
        </TabsContent>

        {isMe && (
          <TabsContent value="wallet" className="mt-4">
            <div data-testid="profile-coin-history" className="rounded-xl border border-border bg-card divide-y divide-border">
              {history.length === 0 && <Empty text="No coin activity yet." />}
              {history.map((t) => (
                <div key={t.id} className="flex items-center gap-3 p-3.5">
                  <div className={`grid place-items-center h-9 w-9 rounded-lg ${t.amount >= 0 ? "bg-emerald_dnjd/15 text-emerald_dnjd" : "bg-destructive/15 text-destructive"}`}>
                    {t.amount >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{t.reason}</div>
                    <div className="text-xs text-muted-foreground font-mono">{timeAgo(t.created_at)}</div>
                  </div>
                  <div className={`font-mono font-bold text-sm inline-flex items-center gap-1 ${t.amount >= 0 ? "text-emerald_dnjd" : "text-destructive"}`}>
                    <Coins size={13} /> {t.amount >= 0 ? "+" : ""}{t.amount}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        )}
      </Tabs>

      {isMe && <EditModal open={editOpen} onOpenChange={setEditOpen} user={p} onSaved={(u) => { patchUser(u); setData((d) => ({ ...d, user: { ...d.user, ...u } })); }} />}
      <AuthModal open={authOpen} tab="register" onOpenChange={setAuthOpen} />
    </div>
  );
}

function Empty({ text }) {
  return <div className="grid place-items-center py-14 text-sm text-muted-foreground">{text}</div>;
}

function EditModal({ open, onOpenChange, user, onSaved }) {
  const [alias, setAlias] = useState(user.alias);
  const [bio, setBio] = useState(user.bio || "");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.patch("/users/me", { alias, bio, avatar_seed: alias });
      onSaved(data);
      toast.success("Profile updated");
      onOpenChange(false);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="edit-profile-modal">
        <DialogHeader><DialogTitle className="font-display">Edit profile</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Alias</label>
            <Input data-testid="edit-alias-input" value={alias} onChange={(e) => setAlias(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Bio</label>
            <Textarea data-testid="edit-bio-input" rows={3} maxLength={280} value={bio} onChange={(e) => setBio(e.target.value)} placeholder="What issues do you care about?" />
          </div>
          <Button type="submit" disabled={saving} data-testid="edit-save-button" className="w-full bg-saffron text-primary-foreground hover:bg-saffron/90 gap-1.5">
            {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />} Save
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
